import React from 'react';
import qs from 'qs';
import Moment from 'moment';

import hoistNonReactStatics from 'hoist-non-react-statics';

import { useHistory } from 'react-router-dom';

import useQueryParams from '../../hooks/routing/useQueryParams';
import type { DateRangeValue, HandleApply } from './helpers/types';
import { DateRangePicker } from './DateRangePicker';

type WithQueryParamControllerProps = {
  initialValue?: DateRangeValue;
};

type InjectedProps = {
  handleApply: HandleApply;
  value: DateRangeValue;
};

export const withQueryParamController = <T extends InjectedProps>(
  Component: React.ComponentType<T>
) => {
  const Wrapper = (
    props: Omit<T, keyof InjectedProps> & WithQueryParamControllerProps
  ) => {
    const history = useHistory();
    const queryParams = useQueryParams();

    const _startDate = queryParams.after
      ? Moment(Number(queryParams.after as string) * 1000).toDate()
      : props.initialValue?.startDate ?? null;

    const _endDate = queryParams.before
      ? Moment(Number(queryParams.before as string) * 1000).toDate()
      : props.initialValue?.endDate ?? null;

    const urlDateRangeValue = {
      startDate: _startDate,
      endDate: _endDate,
    } satisfies DateRangeValue;

    const handleApply: HandleApply = (rangeValue) => {
      const updatedParams = {
        ...queryParams,
        before: Moment(rangeValue.endDate).endOf('day').unix(),
        after: Moment(rangeValue.startDate).startOf('day').unix(),
      };

      history.push({
        search: qs.stringify(updatedParams, { addQueryPrefix: true }),
      });
    };

    const newProps = {
      ...props,
      initialValue: urlDateRangeValue,
      onApply: handleApply,
    } as unknown as T;

    return <Component {...newProps} />;
  };

  const displayName = Component.displayName ?? Component.name ?? 'Component';
  Wrapper.displayName = `withQueryParamController(${displayName})`;
  return hoistNonReactStatics(Wrapper, Component);
};

// @ts-expect-error - ignore for now, needs fixed
export const URLDateRangePicker = withQueryParamController(DateRangePicker);
